<template>
  <div class="border-t border-line bg-surface px-4 py-3 sm:px-6">
    <div class="mx-auto w-full max-w-3xl">
      <div v-if="attachments.length" class="mb-2.5 flex flex-wrap gap-2">
        <div v-for="(a, i) in attachments" :key="i" class="relative">
          <img :src="a.dataUrl" alt="" class="size-12 rounded-lg border border-line-strong object-cover" />
          <button
            type="button"
            class="absolute -right-1.5 -top-1.5 flex size-4 items-center justify-center rounded-full bg-gray-700 text-white"
            title="Remove"
            @click="attachments.splice(i, 1)"
          >
            <X class="size-2.5" />
          </button>
        </div>
      </div>
      <div class="mb-1.5 flex items-center justify-end">
        <button
          type="button"
          class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium transition-colors"
          :class="autoRunOn ? 'bg-brand-50 text-brand-700' : 'text-faint hover:text-muted'"
          :title="$t('chat-ui.autoRun.hint')"
          @click="toggleAutoRun"
        >
          <Zap class="size-3" />
          {{ autoRunOn ? $t('chat-ui.autoRun.on') : $t('chat-ui.autoRun.off') }}
        </button>
      </div>
      <form
        class="flex items-end gap-2 rounded-2xl border border-line-strong bg-surface p-1.5 transition-colors focus-within:border-brand-500 focus-within:ring-3 focus-within:ring-brand-500/20"
        @submit.prevent="onSubmit"
      >
        <button
          type="button"
          class="flex size-9 shrink-0 items-center justify-center rounded-full text-muted transition-colors hover:bg-surface-2 hover:text-ink disabled:opacity-50"
          title="Attach image"
          :disabled="disabled"
          @click="fileInput?.click()"
        >
          <Paperclip class="size-4" />
        </button>
        <textarea
          ref="ta"
          v-model="text"
          rows="1"
          class="max-h-[200px] flex-1 resize-none border-0 bg-transparent px-1 py-2 text-sm text-ink placeholder:text-faint focus:outline-none focus:ring-0"
          placeholder="Ask the assistant…  (Enter to send · Shift+Enter for newline)"
          :disabled="disabled"
          @input="autoResize"
          @paste="onPaste"
          @keydown.enter.exact.prevent="onSubmit"
        />
        <!-- Streaming → the send button becomes STOP (aborts the turn). -->
        <Button
          v-if="loading"
          type="button"
          class="!size-9 !rounded-full !p-0"
          title="Stop generating"
          @click="emit('stop')"
        >
          <template #icon><Square class="size-3.5 fill-current" /></template>
        </Button>
        <Button
          v-else
          type="submit"
          class="!size-9 !rounded-full !p-0"
          :disabled="(!text.trim() && !attachments.length) || disabled"
          title="Send"
        >
          <template #icon><Send class="size-4" /></template>
        </Button>
        <input ref="fileInput" type="file" accept="image/png,image/jpeg,image/webp" multiple hidden @change="onFiles" />
      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue';
import { Paperclip, Send, Square, X, Zap } from 'lucide-vue-next';
import Button from '../Button.vue';
import { useChatStore, type ChatAttachment, type ImgMedia } from '../../stores/chat';

// Per-session auto-run toggle: on → run actions without a confirm card this
// session; off → inherit the workspace default. Device write-back still confirms.
const chat = useChatStore();
const autoRunOn = computed(() => chat.autoRun === true);
function toggleAutoRun(): void { chat.autoRun = chat.autoRun === true ? null : true; }

defineProps<{
  loading: boolean;
  disabled: boolean;
}>();

const emit = defineEmits<{
  send: [text: string, images: ChatAttachment[]];
  /** Stop the in-flight assistant turn (shown while `loading`). */
  stop: [];
  error: [message: string];
}>();

const text = ref('');
const ta = ref<HTMLTextAreaElement | null>(null);
const fileInput = ref<HTMLInputElement | null>(null);
const attachments = ref<ChatAttachment[]>([]);

const OK_MEDIA: ImgMedia[] = ['image/png', 'image/jpeg', 'image/webp'];

function autoResize(): void {
  const el = ta.value;
  if (!el) return;
  el.style.height = 'auto';
  el.style.height = Math.min(el.scrollHeight, 200) + 'px';
}

async function addFile(file: File): Promise<void> {
  if (!OK_MEDIA.includes(file.type as ImgMedia)) {
    emit('error', `Tipo no soportado (${file.type || 'desconocido'}): usa PNG, JPEG o WebP.`);
    return;
  }
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(String(r.result));
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
  const dataBase64 = dataUrl.split(',')[1] ?? '';
  if (dataBase64) attachments.value.push({ mediaType: file.type as ImgMedia, dataUrl, dataBase64 });
}

async function onFiles(e: Event): Promise<void> {
  const input = e.target as HTMLInputElement;
  for (const f of Array.from(input.files ?? [])) await addFile(f);
  input.value = '';
}

async function onPaste(e: ClipboardEvent): Promise<void> {
  const imgs = Array.from(e.clipboardData?.items ?? []).filter((it) => it.kind === 'file' && it.type.startsWith('image/'));
  if (!imgs.length) return;
  e.preventDefault();
  for (const it of imgs) { const f = it.getAsFile(); if (f) await addFile(f); }
}

function onSubmit(): void {
  const t = text.value.trim();
  const imgs = attachments.value.slice();
  if (!t && !imgs.length) return;
  emit('send', t, imgs);
  text.value = '';
  attachments.value = [];
  nextTick(() => autoResize());
}
</script>
